import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { siteConfig } from "../../config/siteConfig";
import SealChip from "../ui/SealChip";

const LoginModal = ({
  isOpen,
  onClose,
  onSwitchToRegister,
  onForgotPassword,
}) => {
  const { login } = useAuth();
  const GOOGLE_TXT = {
    es: {
      btn: "Jatka Google-tilillä",
      or: "o",
      err: "Google-kirjautuminen epäonnistui. Yritä uudelleen.",
    },
    en: {
      btn: "Continue with Google",
      or: "or",
      err: "Could not sign in with Google. Please try again.",
    },
    de: {
      btn: "Weiter mit Google",
      or: "oder",
      err: "Anmeldung mit Google fehlgeschlagen. Bitte erneut versuchen.",
    },
    fr: {
      btn: "Continuer avec Google",
      or: "ou",
      err: "Connexion avec Google impossible. R\u00e9essayez.",
    },
    tr: {
      btn: "Google ile devam et",
      or: "veya",
      err: "Google ile giri\u015f yap\u0131lamad\u0131. L\u00fctfen tekrar deneyin.",
    },
  };
  const googleTxt =
    GOOGLE_TXT[(siteConfig.brand?.locale || "es").slice(0, 2)] || GOOGLE_TXT.es;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mounted, setMounted] = useState(false);
  const ERROR_MESSAGES = {
    "Invalid login credentials":
      "Virheelliset tunnukset. Tarkista sähköposti ja salasana.",
    network: "Ei yhteyttä. Tarkista verkkoyhteys ja yritä uudelleen.",
    default: "Kirjautumisessa tapahtui virhe. Yritä uudelleen.",
  };

  // Detecta fallos de red (Supabase no responde) para distinguirlos de
  // credenciales inválidas y mostrar un mensaje específico de conexión.
  const isNetworkError = (raw) =>
    /failed to fetch|networkerror|network error|load failed/i.test(
      String(raw || "")
    );
  const auth = siteConfig.uiCopy.auth;
  // Sceau de réassurance (language-agnostic, fallback sûr si la clé n'existe pas).
  const secureLabel = auth?.secureLabel || "Suojattu käyttöoikeus";

  const panelRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  const forceScrollToTop = () => {
    const mainScroll = document.getElementById("main-scroll");
    if (mainScroll) mainScroll.scrollTop = 0;
    window.scrollTo({ top: 0, behavior: "instant" });
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const result = await login(email, password);

      if (result.success) {
        forceScrollToTop();
        onClose();
      } else {
        const errorCode = result.error?.code || result.error;
        const translatedError = isNetworkError(errorCode)
          ? ERROR_MESSAGES["network"]
          : ERROR_MESSAGES[errorCode] || ERROR_MESSAGES["default"];
        setError(translatedError);
      }
    } catch (err) {
      setError(
        isNetworkError(err?.message)
          ? ERROR_MESSAGES["network"]
          : ERROR_MESSAGES["default"]
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseToLanding = () => {
    onClose();
    setIsLoading(false);
  };

  // Scale-in 95->100 au montage (transform/opacity only -> 0 reflow, neutralisé
  // sous prefers-reduced-motion par la règle globale d'index.css).
  useEffect(() => {
    if (!isOpen) {
      setMounted(false);
      return;
    }
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, [isOpen]);

  // Accesibilidad del diálogo modal: foco inicial, cierre con Escape,
  // focus trap (Tab/Shift+Tab) y restauración del foco al cerrar.
  useEffect(() => {
    if (!isOpen) return;

    previouslyFocusedRef.current = document.activeElement;

    const panel = panelRef.current;
    const getFocusable = () =>
      panel
        ? Array.from(
            panel.querySelectorAll(
              'a[href], button:not([disabled]), textarea, input:not([disabled]), select, [tabindex]:not([tabindex="-1"])'
            )
          )
        : [];

    const focusable = getFocusable();
    if (focusable.length > 0) {
      focusable[0].focus();
    } else if (panel) {
      panel.focus();
    }

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleCloseToLanding();
        return;
      }
      if (e.key === "Tab") {
        const items = getFocusable();
        if (items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey) {
          if (
            document.activeElement === first ||
            !panel.contains(document.activeElement)
          ) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (
            document.activeElement === last ||
            !panel.contains(document.activeElement)
          ) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      const prev = previouslyFocusedRef.current;
      if (prev && typeof prev.focus === "function") {
        prev.focus();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] overflow-y-auto">
      <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md animate-fade" />

      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="login-title"
          className={`relative w-full max-w-md transform overflow-hidden rounded-2xl bg-white elev-3 transition-all duration-300 ease-out ${
            mounted ? "scale-100 opacity-100" : "scale-95 opacity-0"
          }`}
        >
          <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-teal-500 via-cyan-500 to-emerald-500" />
          <button
            onClick={handleCloseToLanding}
            className="absolute top-4 right-4 p-2 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors z-10"
            aria-label="Sulje ja palaa etusivulle"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
          <div className="px-8 pt-10 pb-8">
            <div className="flex justify-center mb-5">
              <div className="bg-gradient-to-br from-teal-700 to-cyan-600 p-4 rounded-2xl elev-2">
                <svg
                  className="w-10 h-10 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </div>
            </div>

            <h2
              id="login-title"
              className="text-3xl font-extrabold text-center text-slate-900 mb-2"
            >
              {auth.welcomeTitle}
            </h2>
            <p className="text-center text-slate-500 mb-4">
              {auth.welcomeSubtitle}
            </p>

            <div className="flex justify-center mb-7">
              <SealChip label={secureLabel} icon="lock" tone="teal" />
            </div>
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div
                  role="alert"
                  className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3"
                >
                  <svg
                    className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="text-sm text-red-700">{error}</span>
                </div>
              )}

              <div>
                <label
                  htmlFor="login-email"
                  className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2"
                >
                  Sähköposti
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-4 flex items-center text-slate-400">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </span>
                  <input
                    id="login-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="sinun@sähköposti.fi"
                    disabled={isLoading}
                    className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    required
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="login-password"
                  className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2"
                >
                  Salasana
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-4 flex items-center text-slate-400">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                  </span>
                  <input
                    id="login-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    disabled={isLoading}
                    className="w-full pl-12 pr-12 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:bg-white focus:border-teal-500 focus:ring-2 focus:ring-teal-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={
                      showPassword ? "Piilota salasana" : "Näytä salasana"
                    }
                    aria-pressed={showPassword}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showPassword ? (
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        focusable="false"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        focusable="false"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500 focus:ring-offset-0"
                  />
                  <span className="text-xs text-slate-600 group-hover:text-slate-800 transition-colors">
                    Muista minut
                  </span>
                </label>
                <button
                  type="button"
                  onClick={onForgotPassword}
                  className="text-xs font-semibold text-teal-700 hover:text-teal-800 transition-colors"
                >
                  Unohditko salasanasi?
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full py-4 text-base disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <span
                      className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"
                      aria-hidden="true"
                    />
                    Kirjaudutaan sisään...
                  </>
                ) : (
                  "Kirjaudu sisään"
                )}
              </button>
            </form>

            <p className="text-center text-sm text-slate-600 mt-8">
              Eikö sinulla ole tiliä?{" "}
              <button
                onClick={onSwitchToRegister}
                className="font-semibold text-teal-700 hover:text-teal-800 transition-colors"
              >
                Rekisteröidy ilmaiseksi
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
